import styled from 'styled-components';
import { Dialog, DialogMessage } from '@elementary/common-ui/components';
import { capitalize } from '@elementary/common-ui/utils/common';
import { useMutation } from '@tanstack/react-query';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import * as api from 'shared/services/api';
import { ReactComponent as DisabledIcon } from '@elementary/common-ui/assets/16x16/disabled.svg';
import { Typography } from '@mui/material';

const Bold = styled(Typography).attrs(() => ({ component: 'span' }))`
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  margin-inline: 4px;
  font-size: inherit;
`;

type Props = {
  open: boolean;
  testUniqueId: string;
  testName: string;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'disable' | 'enable';
};

const ToggleDisableTestDialog = ({
  open,
  testUniqueId,
  testName,
  onClose,
  onSuccess,
  mode,
}: Props) => {
  const { openSnackbar } = useSnackbar();
  const selectedEnvId = useSelectedEnv();

  const { mutateAsync: updateTest, isLoading: isUpdateLoading } = useMutation(
    async (id: string) => {
      await api.updateTest(selectedEnvId!, id, {
        disabled: mode === 'disable',
      });
    },
    {
      onSuccess: () => {
        openSnackbar({
          message: `Test was ${
            mode === 'disable' ? 'disabled' : 'enabled'
          } successfully`,
          variant: 'success',
        });
        onSuccess();
      },
      onError: () => {
        openSnackbar({
          message: `Failed to ${mode} test`,
          variant: 'error',
        });
      },
    },
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onConfirmButtonClick={() => {
        updateTest(testUniqueId);
        onClose();
      }}
      isLoading={isUpdateLoading}
      titleIcon={<DisabledIcon />}
      title={`${capitalize(mode)} Test`}
      confirmText={capitalize(mode)}
      message={
        <DialogMessage>
          {`Are you sure you want to ${mode} `}
          <Bold>{`'${testName}'`}</Bold>
          {' from '}
          <Bold>Table tests</Bold>?
        </DialogMessage>
      }
      secondaryMessage={`You can always re-${
        mode === 'disable' ? 'enable' : 'disable'
      } the test from this screen`}
    />
  );
};

export default ToggleDisableTestDialog;
